import {
  CLUB_LANDING_PAGE,
  COACHES_LANDING_PAGE,
  LOGIN_PAGE,
  SIGNUP_CODE_PAGE,
} from 'constants/pages';
import { Button, ButtonLink, ButtonLinkText, ButtonText } from 'components/Button';
import classNames from 'styles/utils/classNames';

interface Props {
  /**
   * When true, use <Link> navigation to /login instead of opening the modal.
   * Used on specific signup/login-adjacent flows.
   */
  shouldLinkToAuthPage?: boolean;
  isInvert?: boolean;
  /**
   * When true, hide the "Log in" CTA. Used when the nav search bar has taken
   * over and space is tight — only the "Sign up" button remains on the right.
   */
  isCompact?: boolean;
  /**
   * When true, hide the coach / club pills and divider on narrow viewports.
   * They are rendered in the mobile second row instead.
   */
  shouldHidePillsOnMobile?: boolean;
  onOpenLoginModal: () => void;
  onOpenSignupModal: () => void;
}

const textButtonClass = (isInvert?: boolean) =>
  classNames(
    'rounded-full px-4 py-2.5 font-medium transition-colors hover:bg-color-bg-lightmode-secondary hover:dark:bg-color-bg-darkmode-secondary',
    isInvert
      ? 'text-color-text-darkmode-secondary dark:text-color-text-lightmode-secondary'
      : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
  );

const pillButtonClass = (isInvert?: boolean) =>
  classNames(
    'inline-flex items-center gap-0.5 rounded-full px-2 py-2.5 font-medium transition-colors hover:bg-color-bg-lightmode-secondary hover:dark:bg-color-bg-darkmode-secondary',
    isInvert
      ? 'text-color-text-darkmode-secondary dark:text-color-text-lightmode-secondary'
      : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
  );

export default function AnonAuthButtons({
  shouldLinkToAuthPage,
  isInvert,
  isCompact,
  shouldHidePillsOnMobile,
  onOpenLoginModal,
  onOpenSignupModal,
}: Props) {
  if (shouldLinkToAuthPage) {
    return (
      <div className="flex h-full shrink-0 items-center space-x-2">
        <ButtonLinkText href={LOGIN_PAGE} size="md" className={textButtonClass(isInvert)}>
          Log in
        </ButtonLinkText>
        <ButtonLink
          href={SIGNUP_CODE_PAGE}
          variant={isInvert ? 'inverted' : 'primary'}
          size="sm"
          isInline
        >
          Sign up
        </ButtonLink>
      </div>
    );
  }

  return (
    <div className="flex h-full shrink-0 items-center space-x-2">
      {!isCompact && (
        <>
          <div
            className={classNames(
              'flex items-center',
              shouldHidePillsOnMobile && 'hidden @[1024px]:flex',
            )}
          >
            <ButtonLinkText
              href={COACHES_LANDING_PAGE}
              size="md"
              className={pillButtonClass(isInvert)}
            >
              <img className="h-3.5 w-3.5" src="/images/app/cap-colored.png" />
              I&apos;m a coach
            </ButtonLinkText>

            <ButtonLinkText
              href={CLUB_LANDING_PAGE}
              size="md"
              className={pillButtonClass(isInvert)}
            >
              <img className="h-3.5 w-3.5" src="/images/app/stadium.png" />
              I&apos;m a club
            </ButtonLinkText>
          </div>
          <div
            className={classNames(
              'w-px self-stretch bg-color-border-input-lightmode',
              shouldHidePillsOnMobile && 'hidden @[1024px]:block',
            )}
          />
        </>
      )}
      <ButtonText
        onClick={() => onOpenLoginModal()}
        size="md"
        className={textButtonClass(isInvert)}
      >
        Log in
      </ButtonText>
      <Button
        onClick={() => onOpenSignupModal()}
        variant={isInvert ? 'inverted' : 'primary'}
        size="sm"
        isInline
      >
        Sign up
      </Button>
    </div>
  );
}

/**
 * Just the two "I'm a coach" / "I'm a club" pills — reused in the mobile
 * second row of the nav when the main row is too cramped to fit them.
 */
export function AnonAuthPills({
  isInvert,
  onOpenLoginModal,
}: {
  isInvert?: boolean;
  onOpenLoginModal: () => void;
}) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      <ButtonLinkText size="md" className={pillButtonClass(isInvert)} href={COACHES_LANDING_PAGE}>
        <img className="h-3.5 w-3.5" src="/images/app/cap-colored.png" />
        I&apos;m a coach
      </ButtonLinkText>

      <ButtonLinkText size="md" className={pillButtonClass(isInvert)} href={CLUB_LANDING_PAGE}>
        <img className="h-3.5 w-3.5" src="/images/app/stadium.png" />
        I&apos;m a club
      </ButtonLinkText>
    </div>
  );
}
